import type { RecentTimelineEvent } from '../../../types/cache/timeline-items/recent-events.types';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  CallDocument,
  type TCSearcherTimelineItemDocument,
} from 'src/api/generated';
import {
  getTimelineSearchParams,
  shouldAddTimelineItemToQuery,
} from '../query-matcher.util';
import { sortByCreatedAtDesc } from 'src/modules/shared/utils/sort/created-at-sort.util';

const recentEvents = new WeakMap<
  QueryClient,
  Map<string, RecentTimelineEvent>
>();
const subscribedClients = new WeakSet<QueryClient>();
const RETENTION_MS = 60_000;
const MAX_EVENTS = 500;
const identity = (
  item: Pick<RecentTimelineEvent, 'id' | 'modelType'>
): string => `${item.modelType}:${item.id}`;

const retainCanonicalCall = (
  events: Map<string, RecentTimelineEvent>,
  key: string,
  event: RecentTimelineEvent,
  fetched: TCSearcherTimelineItemDocument
): void => {
  if (
    event.item?.modelType === CallDocument.modelType.CALL &&
    fetched.modelType === CallDocument.modelType.CALL &&
    fetched.tenantId === event.item.tenantId
  ) {
    // Replace replay authority without extending its original retention
    // window or changing snapshot-free deletion semantics.
    events.set(key, {
      ...event,
      item: fetched,
      occurredAt: fetched.updatedAt,
    });
  }
};

const mayNeedEvent = (
  queryKey: QueryKey,
  event: RecentTimelineEvent
): boolean => {
  // A snapshot-free tombstone has no membership metadata. Keep it bounded by
  // the existing TTL/cap while timeline or board queries may still need it.
  const item = event.item;
  if (!item) return true;
  const params = getTimelineSearchParams(queryKey);
  if (!params?.filters || params.filters.some((filter) => filter.or)) {
    return true;
  }
  const filters = params.filters.filter((filter) => {
    if (
      filter.value === undefined ||
      (filter.operator && !['eq', 'in'].includes(filter.operator))
    ) {
      return false;
    }
    if (filter.field === 'funnelConversationId') {
      return Boolean(item.funnelConversationId);
    }
    if (filter.field === 'contactId') return Boolean(item.contactId);
    if (filter.field === 'funnelId') return Boolean(item.funnelId);
    return false;
  });
  // Full-text and unsupported filters can still need an update to an existing
  // result. Only a known, conjunctive identity mismatch proves irrelevance.
  return shouldAddTimelineItemToQuery(
    ['crm/timeline-items/search', { filters }],
    item
  );
};

/**
 * Retains a bounded recent event overlay for HTTP reads that lag realtime writes.
 * @param queryClient - Cache owner; overlays never cross client lifetimes.
 * @param event - Snapshot or tombstone to reconcile with subsequent reads.
 */
export const rememberTimelineEvent = (
  queryClient: QueryClient,
  event: Omit<RecentTimelineEvent, 'expiresAt'>
): void => {
  if (!subscribedClients.has(queryClient)) {
    subscribedClients.add(queryClient);
    queryClient.getQueryCache().subscribe((event) => {
      const queryKey: unknown = event.query.queryKey;
      if (
        event.type === 'removed' &&
        Array.isArray(queryKey) &&
        typeof queryKey[0] === 'string' &&
        [
          'crm/timeline-items/search',
          'crm/documents/funnel-conversations',
        ].includes(queryKey[0])
      ) {
        const remainingQueries = queryClient.getQueryCache().findAll({
          queryKey: ['crm/timeline-items/search'],
        });
        const hasDocumentQueries =
          queryClient.getQueryCache().findAll({
            queryKey: ['crm/documents/funnel-conversations'],
          }).length > 0;
        if (remainingQueries.length === 0 && !hasDocumentQueries) {
          recentEvents.delete(queryClient);
          return;
        }
        const retained = recentEvents.get(queryClient);
        retained?.forEach((entry, key) => {
          if (
            !(
              hasDocumentQueries &&
              (!entry.item ||
                entry.item.modelType === CallDocument.modelType.CALL)
            ) &&
            !remainingQueries.some((query) =>
              mayNeedEvent(query.queryKey, entry)
            )
          ) {
            retained.delete(key);
          }
        });
      }
    });
  }
  let events = recentEvents.get(queryClient);
  if (!events) {
    events = new Map();
    recentEvents.set(queryClient, events);
  }
  events.delete(identity(event));
  events.set(identity(event), {
    ...event,
    expiresAt: Date.now() + RETENTION_MS,
  });
  if (events.size > MAX_EVENTS) {
    const oldestKey = events.keys().next().value;
    if (oldestKey !== undefined) events.delete(oldestKey);
  }
};

/**
 * Reconciles a fetched timeline page with recent updates and deletions.
 * Missing recent items are inserted only on the first page; newer HTTP values win.
 * @param queryClient - Cache owner used by the timeline query.
 * @param queryKey - Full search key, including membership filters.
 * @param items - Server timeline page.
 * @param firstPage - Whether this read is the first page of the search.
 * @returns Sorted snapshots with recent writes preserved during index lag.
 */
export const reconcileRecentTimelineEvents = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  items: TCSearcherTimelineItemDocument[],
  firstPage: boolean
): TCSearcherTimelineItemDocument[] => {
  const events = recentEvents.get(queryClient);
  if (!events) return items;
  const byIdentity = new Map(items.map((item) => [identity(item), item]));
  for (const [key, event] of events) {
    if (event.expiresAt <= Date.now()) {
      events.delete(key);
      continue;
    }
    const fetched = byIdentity.get(key);
    if (
      fetched &&
      (Date.parse(fetched.updatedAt) > Date.parse(event.occurredAt) ||
        (event.modelType === CallDocument.modelType.CALL &&
          Date.parse(fetched.updatedAt) === Date.parse(event.occurredAt)))
    ) {
      retainCanonicalCall(events, key, event, fetched);
      continue;
    }
    if (!event.item) {
      byIdentity.delete(key);
    } else if (
      fetched ||
      (firstPage && shouldAddTimelineItemToQuery(queryKey, event.item))
    ) {
      byIdentity.set(key, event.item);
    }
  }
  return sortByCreatedAtDesc([...byIdentity.values()]);
};

/**
 * Checks accepted deletion authority within the existing overlay TTL and cap.
 * @param queryClient - Account-owned cache, shared by timeline and board views.
 * @param tenantId - Tenant of the fetched document.
 * @param id - Preview Message identity.
 * @returns Whether this tenant's Message is still retained as deleted.
 */
export const hasRecentMessageDeletion = (
  queryClient: QueryClient,
  tenantId: string,
  id: string
): boolean => {
  const events = recentEvents.get(queryClient);
  const key = `Message:${id}`;
  const event = events?.get(key);
  if (!event) return false;
  if (event.expiresAt <= Date.now()) {
    events?.delete(key);
    return false;
  }
  return !event.item && event.tenantId === tenantId;
};

/**
 * Reads accepted Call authority from the existing capped, expiring overlay.
 * @param queryClient - Account-owned cache shared by timeline and board views.
 * @param tenantId - Tenant of the fetched row.
 * @param id - Existing Call preview identity; never selects a replacement interaction.
 * @returns Retained same-tenant Call, when still within the recovery window.
 */
export const getRecentCall = (
  queryClient: QueryClient,
  tenantId: string,
  id: string
): CallDocument | undefined => {
  const events = recentEvents.get(queryClient);
  const key = `Call:${id}`;
  const event = events?.get(key);
  if (!event) return undefined;
  if (event.expiresAt <= Date.now()) {
    events?.delete(key);
    return undefined;
  }
  return event.item?.modelType === CallDocument.modelType.CALL &&
    event.item.tenantId === tenantId
    ? event.item
    : undefined;
};
